import type { ResolvedMonospaceDataSource } from '#layers/data-model/types/schema/resolved-schema';
import { readonly, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useWorkspaceSchemaStore } from '#layers/data-model/stores/workspace-schema';
import { useWorkspaceRuntimeHandle } from '#layers/runtime/public';
import { useToastResultHandling } from '#layers/ui/public';

interface UseDisconnectDataSourceOptions {
	/**
	 * Runs after the disconnect succeeded but before the schema refresh drops the
	 * source, so callers rendered off that source can navigate away first.
	 */
	onDisconnected?: () => void | Promise<void>;
}

/**
 * Disconnects a data source and refreshes the workspace schema, surfacing success or
 * failure as a toast.
 */
export function useDisconnectDataSource(options: UseDisconnectDataSourceOptions = {}) {
	const { t } = useI18n();
	const { refresh: refreshSchema } = useWorkspaceSchemaStore();
	const { onSuccess, onError } = useToastResultHandling({
		success: { title: t('pages.data_model.disconnect_source_success.title') },
		error: { title: t('pages.data_model.disconnect_source_error.title') },
	});

	const isDisconnecting = ref(false);

	async function disconnectDataSource(source: ResolvedMonospaceDataSource) {
		const handle = useWorkspaceRuntimeHandle();
		const { fetch } = handle;

		isDisconnecting.value = true;

		return handle.run(async () => {
			try {
				await fetch(`sources/data/${source.id}`, { method: 'DELETE' });

				if (!handle.isActive()) return;

				await options.onDisconnected?.();

				if (!handle.isActive()) return;

				onSuccess();
				await refreshSchema();
			}
			catch (error) {
				if (handle.isActive()) onError(error);
			}
			finally {
				isDisconnecting.value = false;
			}
		});
	}

	return { disconnectDataSource, isDisconnecting: readonly(isDisconnecting) };
}
